import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, UserPlus, Check } from "lucide-react";
import { API } from "@/lib/api";

const GREETING = {
  role: "assistant",
  content:
    "Hi! I'm the Big Pivot assistant. Ask me about our services, our work, or how we can help — or request a free report analysis.",
};

const STARTERS = [
  "What services do you offer?",
  "Show me your recent work",
  "How does the free report analysis work?",
];

const getSessionId = () => {
  let sid = sessionStorage.getItem("bpp_chat_sid");
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem("bpp_chat_sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", note: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadError, setLeadError] = useState("");
  const sidRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  if (!sidRef.current) sidRef.current = getSessionId();

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, open, scrollToBottom]);

  useEffect(() => {
    if (!open || loadedHistory) return;
    setLoadedHistory(true);
    fetch(`${API}/chat/${sidRef.current}/history`)
      .then((r) => (r.ok ? r.json() : []))
      .then((h) => {
        if (Array.isArray(h) && h.length) {
          setMessages(h.map((m) => ({ role: m.role, content: m.content })));
        }
      })
      .catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 250);
  }, [open, loadedHistory]);

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || streaming) return;
    if (typeof preset !== "string") setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          let data;
          try { data = JSON.parse(line); } catch { continue; }
          if (data.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + data.delta };
              return c;
            });
          } else if (data.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], content: data.error };
              return c;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        const last = c[c.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          c[c.length - 1] = { ...last, content: "Sorry, I couldn't connect just now. Please try again, or email joel@bigpivot.net." };
        }
        return c;
      });
    } finally {
      setStreaming(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const submitLead = async () => {
    const name = lead.name.trim();
    const email = lead.email.trim();
    if (!name || !email || leadBusy) return;
    setLeadBusy(true);
    setLeadError("");
    try {
      const res = await fetch(`${API}/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, name, email, note: lead.note.trim() || undefined }),
      });
      if (!res.ok) throw new Error("bad");
      setShowLead(false);
      setLead({ name: "", email: "", note: "" });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Thanks, ${name}! The team will follow up at ${email} shortly. In the meantime, feel free to keep exploring or ask me anything else.` },
      ]);
    } catch {
      setLeadError("Something went wrong. Please try again or email joel@bigpivot.net.");
    } finally {
      setLeadBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        aria-label={open ? "Close chat" : "Chat with Big Pivot"}
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#FD8924] text-white shadow-[0_14px_40px_-10px_rgba(253,137,36,0.7)] hover:bg-[#D7741F] active:scale-95 transition-[background-color,transform] duration-200"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-[60] flex w-[calc(100vw-2.5rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D1B2A] shadow-[0_30px_80px_-24px_rgba(0,0,0,0.7)]"
          style={{ height: "min(560px, calc(100vh - 8rem))" }}
        >
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FD8924]/15 text-[#FD8924]">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="font-display text-sm font-bold text-white">Ask Big Pivot</div>
              <div className="text-[11px] text-white/50">AI assistant · typically replies in seconds</div>
            </div>
            <button
              type="button" onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat"
              className="ml-auto text-white/50 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-lenis-prevent
            className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#FD8924] text-white rounded-br-sm"
                      : "bg-white/10 text-white/90 rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1 py-1" aria-label="Assistant is typing">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60 [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60 [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60" />
                    </span>
                  ) : "")}
                </div>
              </div>
            ))}
            {messages.length === 1 && !streaming && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-starters">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    data-testid="chat-starter"
                    className="rounded-full border border-[#FD8924]/40 bg-[#FD8924]/10 px-3 py-1.5 text-xs font-medium text-[#FD8924] hover:bg-[#FD8924]/20 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3">
            {showLead ? (
              <div data-testid="chat-lead-form" className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#FD8924]">Leave your details</span>
                  <button type="button" onClick={() => { setShowLead(false); setLeadError(""); }} data-testid="chat-lead-cancel" className="text-white/50 hover:text-white transition-colors"><X className="h-4 w-4" /></button>
                </div>
                <input
                  value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                  data-testid="chat-lead-name" placeholder="Your name"
                  className="w-full rounded-lg bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:bg-white/10 transition-colors"
                />
                <input
                  value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                  data-testid="chat-lead-email" type="email" placeholder="Email address"
                  className="w-full rounded-lg bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:bg-white/10 transition-colors"
                />
                <textarea
                  value={lead.note} onChange={(e) => setLead((l) => ({ ...l, note: e.target.value }))}
                  rows={2} data-testid="chat-lead-note" placeholder="What can we help with? (optional)"
                  className="w-full resize-none rounded-lg bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:bg-white/10 transition-colors"
                />
                {leadError && <p className="text-xs font-semibold text-red-400" data-testid="chat-lead-error">{leadError}</p>}
                <button
                  type="button" onClick={submitLead} disabled={!lead.name.trim() || !lead.email.trim() || leadBusy}
                  data-testid="chat-lead-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FD8924] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#D7741F] active:scale-[0.99] disabled:opacity-40 transition-[background-color,transform] duration-200"
                >
                  {leadBusy ? "Sending…" : (<><Check className="h-4 w-4" /> Send to the team</>)}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2 rounded-xl bg-white/5 px-3 py-2 focus-within:bg-white/10 transition-colors">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    data-testid="chat-input"
                    placeholder="Ask about our services or work…"
                    className="max-h-24 flex-1 resize-none bg-transparent text-sm text-white placeholder:text-white/40 outline-none"
                  />
                  <button
                    type="button" onClick={() => send()} disabled={!input.trim() || streaming}
                    data-testid="chat-send" aria-label="Send message"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FD8924] text-white hover:bg-[#D7741F] active:scale-95 disabled:opacity-40 disabled:active:scale-100 transition-[background-color,transform] duration-200"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                <button
                  type="button" onClick={() => { setShowLead(true); setLeadError(""); }}
                  data-testid="chat-lead-toggle"
                  className="mt-2 flex w-full items-center justify-center gap-1.5 text-[11px] font-medium text-white/50 hover:text-[#FD8924] transition-colors"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Leave your details for a follow-up
                </button>
                <p data-testid="chat-ai-disclosure" className="mt-2 text-center text-[10px] leading-snug text-white/35">
                  Automated AI assistant. Responses are informational, not professional advice.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
